/**
 * Components shared by booker-related UIs
 */

import React from 'react';
import styled from 'styled-components';
import { customClassNames } from '../../utils';

export const BookerContainer = styled.div.attrs<{ className?: string }>({
    className: `bg-default dark:bg-muted grid max-w-full items-start dark:[color-scheme:dark] sm:motion-reduce:transition-none md:flex-row rounded-md sm:transition-[width] sm:duration-300 border-subtle border ${customClassNames.bookerContainer}`,
})`
    grid-template-areas: 'meta main timeslots' 'meta main timeslots';
    width: calc(
        var(--booker-meta-width) + var(--booker-main-width) +
            var(--booker-timeslots-width)
    );
    grid-template-columns: var(--booker-meta-width) 1fr var(
            --booker-timeslots-width
        );
    grid-template-rows: 1fr 0fr;
    min-height: 450px;
    height: auto;
`;

export const SectionContainer = styled.div`
    opacity: 1;
    transform: none;
`;

export const MetaSectionContainer = styled(SectionContainer).attrs({
    className: '[grid-area:meta]',
})`
    min-width: 100%;
`;

export const MainSectionContainer = styled(SectionContainer).attrs({
    className:
        '[grid-area:main] md:border-subtle ml-[-1px] h-full flex-shrink px-5 py-3 md:border-l lg:w-[var(--booker-main-width)]',
})``;

export const TimeslotsContainer = styled(SectionContainer).attrs({
    className:
        '[grid-area:timeslots] border-subtle rtl:border-default flex w-full flex-col px-5 py-3 pb-0 rtl:border-r ltr:md:border-l h-full overflow-hidden md:w-[var(--booker-timeslots-width)]',
})``;

export const EventHeading = styled.p.attrs({
    className: 'text-subtle mt-2 text-sm font-semibold',
})`
    font-size: 20px;
    font-weight: 600;
`;

export const EventSubheading = styled.h1.attrs({
    className: `text-text text-xl font-semibold ${customClassNames.eventMetaCustomClassNames.eventMetaTitle} my-2`,
})`
    margin-top: 16px;
`;

export const Days = styled.div.attrs({
    className:
        'w-full border-subtle mb-2 grid grid-cols-7 gap-4 border-b border-t text-center md:mb-0 md:border-0',
})``;

export const Day = ({ day }: { day: string }) => (
    <div
        className={`text-emphasis my-4 text-xs font-medium uppercase tracking-widest ${customClassNames.datePickerCustomClassNames.datePickerDays}`}
    >
        {day}
    </div>
);

export const DateGrid = styled.div.attrs({
    className: 'relative grid grid-cols-7 grid-rows-6 gap-1 text-center',
})``;

export const DateCellSkeleton = () => (
    <div className='relative w-full pt-[100%]'>
        <button
            className='bg-muted text-muted absolute bottom-0 left-0 right-0 top-0 mx-auto flex w-full items-center justify-center rounded-sm border-transparent text-center font-medium opacity-90 transition'
            disabled
        >
            <span className="font-size-0 bg-emphasis inline-block animate-pulse rounded-md empty:before:inline-block empty:before:content-[''] h-8 w-9" />
        </button>
    </div>
);

export const DateCell = ({
    children,
    isPreviousDay,
    isCurrentDay,
}: {
    children: React.ReactNode;
    isPreviousDay: boolean;
    isCurrentDay: boolean;
}) => {
    let buttonClassName =
        'disabled:text-bookinglighter absolute bottom-0 left-0 right-0 top-0 mx-auto w-full rounded-md border-2 border-transparent text-center text-sm font-medium transition disabled:cursor-default disabled:border-transparent disabled:font-light';

    if (!isPreviousDay) {
        buttonClassName += ' hover:border-brand-default';
    }

    if (!isCurrentDay && !isPreviousDay) {
        buttonClassName += ` ${customClassNames.datePickerCustomClassNames.datePickerDatesActive}`;
    }

    return (
        <div className='relative w-full pt-[100%]'>
            <button
                className={buttonClassName}
                data-disabled={isPreviousDay ? 'true' : 'false'}
                disabled={isPreviousDay}
            >
                {children}
            </button>
        </div>
    );
};

export const DatePickerContainer = styled.div.attrs({
    className: customClassNames.datePickerCustomClassNames.datePickerContainer,
})`
    width: 100%;
`;

export const RowSkeleton = () => (
    <div className="font-size-0 bg-emphasis inline-block animate-pulse rounded-md empty:before:inline-block empty:before:content-[''] mb-4 h-6 w-full" />
);

export const MetaSection = ({ children }: { children: React.ReactNode }) => (
    <MetaSectionContainer>
        <div
            className={`relative p-6 ${customClassNames.eventMetaCustomClassNames.eventMetaContainer}`}
        >
            {children}
        </div>
    </MetaSectionContainer>
);

export const CalendarDays = () => (
    <Days>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <Day key={day} day={day} />
        ))}
    </Days>
);

export const TimeSlotButton = ({ children }: { children: React.ReactNode }) => (
    <div className='flex gap-2 w-full'>
        <button
            data-testid='time'
            data-disabled='false'
            className={`group whitespace-nowrap items-center font-medium relative rounded-[10px] disabled:cursor-not-allowed gap-1 bg-default text-default border border-default enabled:hover:bg-muted enabled:hover:text-emphasis disabled:opacity-30 focus-visible:bg-subtle focus-visible:outline-none focus-visible:ring-0 focus-visible:shadow-outline-gray-focused shadow-outline-gray-rested enabled:hover:shadow-outline-gray-hover enabled:active:shadow-outline-gray-active transition-shadow duration-200 px-2.5 text-sm leading-none hover:border-brand-default min-h-9 mb-2 flex h-auto w-full flex-grow flex-col justify-center py-2 ${customClassNames.availableTimeSlotsCustomClassNames.availableTimes}`}
            type='button'
        >
            <div className='contents visible group-active:translate-y-[0.5px]'>
                <div className='flex items-center gap-2'>{children}</div>
            </div>
        </button>
    </div>
);

export const AvailableTimeSlots = ({
    dayInWeek,
    day,
    slots,
}: {
    dayInWeek: React.ReactNode;
    day: React.ReactNode;
    slots: React.ReactNode;
}) => {
    return (
        <>
            <div
                className={`flex ${customClassNames.availableTimeSlotsCustomClassNames.availableTimeSlotsContainer}`}
            >
                <div className='mb-3 h-8'>
                    <span
                        className={
                            customClassNames.availableTimeSlotsCustomClassNames
                                .availableTimeSlotsTitle
                        }
                    >
                        {dayInWeek}
                    </span>{' '}
                    <span className='available-time-slots-title'>{day}</span>
                </div>
            </div>
            <div
                className={`scroll-bar flex-grow overflow-auto ${customClassNames.availableTimeSlotsCustomClassNames.availableTimeSlotsContainer}`}
            >
                <div className='flex w-[20%] flex-col only:w-full'>{slots}</div>
            </div>
        </>
    );
};

export const DatePickerHeader = ({
    month,
    year,
    actions,
}: {
    month: React.ReactNode;
    year: React.ReactNode;
    actions?: React.ReactNode;
}) => (
    <div className='w-full mb-2 flex items-center justify-between text-xl'>
        <span className='text-default w-1/2 text-base'>
            <span
                className={
                    customClassNames.datePickerCustomClassNames.datePickerTitle
                }
            >
                {month}
            </span>{' '}
            <span
                className={
                    customClassNames.datePickerCustomClassNames.datePickerTitle
                }
            >
                {year}
            </span>
        </span>
        {actions}
    </div>
);
